/**
 * Languages supported by the `page_data/translate` endpoint.
 *
 * Mirrors the `source_language` / `target_language` enum of
 * `POST /api/v1/annotations/{id}/page_data/translate`. Keep this list in sync
 * with the backend OpenAPI spec — sending an unsupported code results in a 400.
 *
 * This module must not import anything from the app: the document store imports
 * it, and `document-translation` imports the document store.
 */

/** Sentinel telling the backend to detect the source language itself. */
export const AUTO_DETECT = 'auto';

export const translateLanguages = [
  'af',
  'sq',
  'am',
  'ar',
  'hy',
  'az',
  'bn',
  'bs',
  'bg',
  'ca',
  'zh',
  'zh-TW',
  'hr',
  'cs',
  'da',
  'fa-AF',
  'nl',
  'en',
  'et',
  'fa',
  'tl',
  'fi',
  'fr',
  'fr-CA',
  'ka',
  'de',
  'el',
  'gu',
  'ht',
  'ha',
  'he',
  'hi',
  'hu',
  'is',
  'id',
  'ga',
  'it',
  'ja',
  'kn',
  'kk',
  'ko',
  'lv',
  'lt',
  'mk',
  'ms',
  'ml',
  'mt',
  'mr',
  'mn',
  'no',
  'ps',
  'pl',
  'pt',
  'pt-PT',
  'pa',
  'ro',
  'ru',
  'sr',
  'si',
  'sk',
  'sl',
  'so',
  'es',
  'es-MX',
  'sw',
  'sv',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
  'cy',
] as const;

export type TranslateLanguage = (typeof translateLanguages)[number];

export type TranslateSourceLanguage = TranslateLanguage | typeof AUTO_DETECT;

const FALLBACK_TARGET_LANGUAGE: TranslateLanguage = 'en';

/**
 * UI locales whose code has no counterpart in the supported list and cannot be
 * resolved by dropping the region subtag.
 */
const languageAliases = new Map<string, TranslateLanguage>([['nb', 'no']]);

const resolveCandidate = (candidate: string): TranslateLanguage | undefined =>
  translateLanguages.find(language => language.toLowerCase() === candidate) ??
  languageAliases.get(candidate);

/**
 * Maps an arbitrary locale tag (`pt-BR`, `zh_CN`, `nb`, …) onto a supported
 * translation language, or `undefined` when there is no match.
 */
export const getTranslateLanguage = (
  locale: string | null | undefined
): TranslateLanguage | undefined => {
  if (!locale) return undefined;

  const normalized = locale.replaceAll('_', '-').toLowerCase();
  const [baseLanguage = normalized] = normalized.split('-');

  return resolveCandidate(normalized) ?? resolveCandidate(baseLanguage);
};

export const resolveDefaultTargetLanguage = (
  profileLocale: string | null | undefined
): TranslateLanguage => {
  const browserLocales = navigator.languages ?? [navigator.language];

  return (
    getTranslateLanguage(profileLocale) ??
    browserLocales
      .map(browserLocale => getTranslateLanguage(browserLocale))
      .find(language => language !== undefined) ??
    FALLBACK_TARGET_LANGUAGE
  );
};

const displayNamesCache = new Map<string, Intl.DisplayNames | undefined>();

const getDisplayNames = (uiLocale: string) => {
  if (!displayNamesCache.has(uiLocale)) {
    try {
      displayNamesCache.set(
        uiLocale,
        new Intl.DisplayNames([uiLocale, 'en'], { type: 'language' })
      );
    } catch {
      displayNamesCache.set(uiLocale, undefined);
    }
  }

  return displayNamesCache.get(uiLocale);
};

/**
 * Localized display name of a language, e.g. `es` → "Spanish" / "španělština".
 * Falls back to the uppercased code when the runtime cannot resolve it.
 */
export const getLanguageLabel = (
  language: TranslateLanguage,
  uiLocale: string
): string => {
  const label = getDisplayNames(uiLocale)?.of(language);

  if (!label || label === language) return language.toUpperCase();

  // Some locales (e.g. Czech) return lowercase language names.
  return label.charAt(0).toLocaleUpperCase(uiLocale) + label.slice(1);
};

export type TranslateLanguageOption = {
  value: TranslateLanguage;
  label: string;
};

/** All supported languages, sorted by their localized label. */
export const getLanguageOptions = (
  uiLocale: string
): TranslateLanguageOption[] => {
  const collator = new Intl.Collator(uiLocale);

  return translateLanguages
    .map(value => ({ value, label: getLanguageLabel(value, uiLocale) }))
    .sort((a, b) => collator.compare(a.label, b.label));
};
